import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Input from '../components/Input';
import PhoneInput from '../components/PhoneInput';
import Button from '../components/Button';
import { APP_NAME } from '../constants';
import logoImage from '../assets/logo.png';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentUser } from '../services/authService';
import { contactService } from '../services/contactService';
import type { User } from '../services/authService';

const PageContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #36226f;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
`;

const Logo = styled.img`
  height: 50px;
  cursor: pointer;
  
  @media (max-width: 480px) {
    height: 40px;
  }
`;

const BackButton = styled.button`
  background-color: #381f74;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: #2a1656;
  }
  
  @media (max-width: 480px) {
    padding: 8px 16px;
    font-size: 14px;
  }
`;

const ContentContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 40px 20px;
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 30px 15px;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #381f74;
  margin-bottom: 10px;
  text-align: center;
  
  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #666;
  text-align: center;
  margin-bottom: 40px;
  line-height: 1.6;
  
  @media (max-width: 480px) {
    font-size: 16px;
    margin-bottom: 30px;
  }
`;

const FormContainer = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
  
  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const TextAreaContainer = styled.div`
  margin-bottom: 20px;
  width: 100%;
`;

const TextAreaLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  font-size: 14px;
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 14px;
  font-size: 16px;
  border: none;
  border-radius: 12px;
  background: #f5f5f5;
  font-family: 'Montserrat', sans-serif;
  outline: none;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  color: #333;
  resize: vertical;

  
  &::placeholder {
    color: #999;
  }
`;

const ErrorText = styled.p`
  color: #e53935;
  margin-top: 6px;
  font-size: 12px;
  font-family: 'Montserrat', sans-serif;
`;

const ContactInfo = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
  
  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const ContactTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #381f74;
  margin-bottom: 20px;
  
  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const ContactItem = styled.div`
  margin-bottom: 15px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ContactLabel = styled.div`
  font-weight: 600;
  color: #381f74;
  margin-bottom: 5px;
`;

const ContactValue = styled.div`
  color: #666;
  font-size: 16px;
`;

const ContactLink = styled.a`
  color: #381f74;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const InfoMessage = styled.div`
  background-color: #e3f2fd;
  color: #1976d2;
  padding: 15px 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 500;
  border: 1px solid #bbdefb;
`;

const StepIndicatorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 30px;
  gap: 20px;
`;

const StepIndicator = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => 
    props.completed ? '#4caf50' : 
    props.active ? '#381f74' : '#e0e0e0'
  };
  color: ${props => 
    props.active || props.completed ? 'white' : '#666'
  };
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s ease;
`;

const StepConnector = styled.div<{ completed: boolean }>`
  width: 40px;
  height: 2px;
  background-color: ${props => props.completed ? '#4caf50' : '#e0e0e0'};
  transition: all 0.3s ease;
`;

const StepContent = styled.div<{ show: boolean }>`
  display: ${props => props.show ? 'block' : 'none'};
  animation: ${props => props.show ? 'fadeIn 0.3s ease-in' : 'none'};
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const StepNavigationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  gap: 15px;
`;

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  subject?: string;
  message?: string;
}

interface StepIndicatorProps {
  currentStep: number;
  totalSteps: number;
}

const ContactUsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user: authUser, loading: authLoading } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 2;
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});// Fetch complete user data when component mounts
  useEffect(() => {
    const fetchUserData = async () => {
      console.log('ContactUsPage: Auth state check:');
      console.log('  - isAuthenticated =', isAuthenticated);
      console.log('  - authUser from context =', authUser);
      console.log('  - authLoading =', authLoading);
      
      if (isAuthenticated && authUser) {
        try {
          console.log('ContactUsPage: User is authenticated, fetching complete user data...');
          const userData = await getCurrentUser();
          console.log('ContactUsPage: Fetched complete user data =', userData);
          setUser(userData);
        } catch (error) {
          console.error('ContactUsPage: Error fetching user data:', error);
          // Don't fallback to authUser due to type mismatch - keep user as null
          console.log('ContactUsPage: Keeping user as null due to fetch error');
          setUser(null);
        }
      } else {
        console.log('ContactUsPage: User not authenticated or no authUser, clearing user data');
        setUser(null);
      }
    };

    console.log('ContactUsPage: useEffect triggered, authLoading =', authLoading);
    // Always run, but log when auth is still loading
    if (authLoading) {
      console.log('ContactUsPage: Auth still loading, waiting...');
      // Set a timeout to log if auth is stuck loading
      const timeout = setTimeout(() => {
        console.warn('ContactUsPage: Auth has been loading for more than 5 seconds. This might indicate an issue.');
      }, 5000);
      
      return () => clearTimeout(timeout);
    } else {
      console.log('ContactUsPage: Auth loading finished, fetching user data...');
      fetchUserData();
    }
  }, [isAuthenticated, authUser, authLoading]);
  // Auto-fill form when user data is loaded
  useEffect(() => {
    console.log('ContactUsPage: Auto-fill effect triggered, user =', user);
    if (user) {
      console.log('ContactUsPage: Auto-filling form with user data:', {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNumber: user.phoneNumber
      });
      setFormData(prev => ({
        ...prev,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phoneNumber || '',
        // Keep subject and message empty as requested
        subject: '',
        message: ''
      }));
    } else {
      console.log('ContactUsPage: No user data, form will remain empty');
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handlePhoneChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      phone: value
    }));
    
    if (errors.phone) {
      setErrors(prev => ({
        ...prev,
        phone: undefined
      }));
    }
  };
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    if (step === 1) {
      // Validate Step 1: First Name, Last Name, Email
      if (!formData.firstName.trim()) {
        newErrors.firstName = 'First name is required';
      }

      if (!formData.lastName.trim()) {
        newErrors.lastName = 'Last name is required';
      }

      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    } else if (step === 2) {
      // Validate Step 2: Phone, Subject, Message
      if (!formData.subject.trim()) {
        newErrors.subject = 'Subject is required';
      }

      if (!formData.message.trim()) {
        newErrors.message = 'Message is required';
      } else if (formData.message.trim().length < 10) {
        newErrors.message = 'Message must be at least 10 characters long';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
    setErrors({}); // Clear errors when going back
  };
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      // Send the contact form via Firebase Function
      await contactService.sendContactEmail({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim()
      });

      console.log('Contact form submitted successfully');
      
      // Navigate to success page instead of showing inline message
      navigate('/contact-success');
    } catch (error: any) {
      console.error('Error submitting contact form:', error);
      // Show error to user
      setErrors({
        message: error.message || 'Failed to send message. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Contact Us</title>
        <meta name="description" content="Contact Store Credit App support team. Get help with your account, business questions, or technical issues." />
        <meta name="keywords" content="contact, support, help, store credit app, customer service" />
      </Helmet>
        <PageContainer>
        <Header>
          <Logo src={logoImage} alt="Logo" onClick={handleLogoClick} />
          {/* <BackButton onClick={handleBackClick}>
            Back
          </BackButton> */}
        </Header>
        
        <ContentContainer>
          <Title>Contact Us</Title>
          <Subtitle>
            Have a question or need help? We're here to assist you! 
            Send us a message and we'll get back to you as soon as possible.
          </Subtitle>      
              {/* {user && (
            <InfoMessage>
              We've pre-filled your contact information. You can edit any field if needed.
            </InfoMessage>
          )} */}

          {/* Step Indicator */}
          <StepIndicatorContainer>
            <StepIndicator active={currentStep === 1} completed={currentStep > 1}>
              1
            </StepIndicator>
            <StepConnector completed={currentStep > 1} />
            <StepIndicator active={currentStep === 2} completed={false}>
              2
            </StepIndicator>
          </StepIndicatorContainer>

          <FormContainer>
            {/* Step 1: Personal Information */}
            <StepContent show={currentStep === 1}>
              <Input
                type="text"
                name="firstName"
                label="First Name"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="Enter your first name"
                required
                error={errors.firstName}
                secondary={user?.firstName ? true : undefined}
              />
              
              <Input
                type="text"
                name="lastName"
                label="Last Name"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Enter your last name"
                required
                error={errors.lastName}
                secondary={user?.lastName ? true : undefined}
              />
              
              <Input
                type="email"
                name="email"
                label="Email Address"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                required
                error={errors.email}
                secondary={user?.email ? true : undefined}
              />              <StepNavigationContainer>
                <div></div> {/* Empty div for spacing */}
                <Button
                  text="Next"
                  onClick={handleNext}
                  bgColor="#381f74"
                  textColor="white"
                  width="auto"
                  minWidth="120px"
                />
              </StepNavigationContainer>
            </StepContent>

            {/* Step 2: Contact Details & Message */}
            <StepContent show={currentStep === 2}>
              <PhoneInput
                name="phone"
                label="Phone Number"
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder="Enter your phone number"
                error={errors.phone}
                secondary={user?.phoneNumber ? true : undefined}
              />

              <Input
                type="text"
                name="subject"
                label="Subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder="What is this regarding?"
                required
                error={errors.subject}
              />

              <TextAreaContainer>
                <TextAreaLabel htmlFor="message">
                  Message <span style={{ color: '#e53935' }}>*</span>
                </TextAreaLabel>
                <StyledTextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Please describe your question or issue in detail..."
                  required
                />
                {errors.message && <ErrorText>{errors.message}</ErrorText>}
              </TextAreaContainer>              <StepNavigationContainer>
                <Button
                  text="Previous"
                  onClick={handlePrevious}
                  outline={true}
                  bgColor="#381f74"
                  textColor="#381f74"
                  width="100%"
                  minWidth="120px"
                />
                <Button
                  text="Send Message"
                  onClick={handleSubmit}
                  loading={loading}
                  bgColor="#381f74"
                  textColor="white"
                  width="100%"
                />
              </StepNavigationContainer>
            </StepContent>
          </FormContainer>
        </ContentContainer>
      </PageContainer>
    </>
  );
};

export default ContactUsPage;
